import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Tooltip from '@mui/material/Tooltip';
import { Link, useLocation } from '@tanstack/react-router';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { NavItem, navItems } from './nav-items';

import { useNovel } from '@/context/novel-context';
import { useUserNovels } from '@/context/user-novels-context';
import { useUserSession } from '@/context/user-session-context';
import { useFirebase } from '@/firebase';
import { useUserProfile } from '@/firebase/hooks/use-user-profile';



interface SidebarNavigationProps {
  onNavItemClick?: () => void;
  collapsed?: boolean;
}

export function SidebarNavigation({ onNavItemClick, collapsed }: SidebarNavigationProps) {
  const { pathname } = useLocation();
  const { hasFeature } = useUserSession();
  const { t } = useTranslation();
  const { user } = useFirebase();
  const { selectedNovelId, novel } = useNovel();
  const { userData } = useUserProfile();

  const activeRole = React.useMemo(() => {
    if (!selectedNovelId || !user) return null;
    if (novel && novel.ownerId === user.uid) return 'owner';
    const collId = selectedNovelId.includes(';') ? selectedNovelId.split(';')[1] : selectedNovelId;
    const collab = userData?.collaboratedNovels?.find((c) => c.novelId === collId);
    return collab?.role || null;
  }, [selectedNovelId, user, novel, userData]);

  const { ownedNovels } = useUserNovels();
  const hasNovels = ownedNovels.length > 0;

  const getHref = (baseHref: string) => {
    return baseHref; // novelId is now handled via NovelContext -> localStorage
  };

  const renderNavGroup = (items: NavItem[], label?: string) => (
    <List
      subheader={
        label && !collapsed ? (
          <ListSubheader
            disableSticky
            sx={{
              bgcolor: 'transparent',
              lineHeight: '32px',
              fontSize: '0.65rem',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              color: 'text.secondary',
              mt: 2,
              mb: 0.5,
            }}
          >
            {label}
          </ListSubheader>
        ) : undefined
      }
      sx={{ px: 0 }}
    >
      {items
        .filter((item) => !item.flag || hasFeature(item.flag))
        .map((item) => {
          const isActive = item.href === '/'
            ? pathname === '/'
            : pathname.startsWith(item.href) || (item.href === '/editor' && pathname.startsWith('/publish-editor'));
          const Icon = item.icon;
          const isDisabled = item.href !== '/' && item.href !== '/library' && !selectedNovelId;

          const getTooltipTitle = () => {
            if (isDisabled) {
              return hasNovels
                ? t('sidebar.tooltips.select_novel')
                : t('sidebar.tooltips.create_novel');
            }
            if (collapsed) {
              return t(item.label);
            }
            return '';
          };

          const button = (
            <ListItemButton
              component={Link}
              to={isDisabled ? '#' : getHref(item.href)}
              onClick={isDisabled ? (e: React.MouseEvent) => e.preventDefault() : onNavItemClick}
              selected={isActive}
              disabled={isDisabled}
              sx={{
                borderRadius: '8px',
                mx: collapsed ? 0.5 : 1,
                py: 1,
                justifyContent: collapsed ? 'center' : 'flex-start',
                opacity: isDisabled ? 0.5 : 1,
                cursor: isDisabled ? 'not-allowed' : 'pointer',
                '&.Mui-disabled': {
                  opacity: 0.5,
                  bgcolor: 'transparent',
                },
                '&.Mui-selected': {
                  bgcolor: 'action.selected',
                  color: 'primary.main',
                  '& .MuiListItemIcon-root': {
                    color: 'primary.main',
                  },
                  '&:hover': {
                    bgcolor: 'action.selected',
                    color: 'primary.main',
                  },
                },
                '&:hover': {
                  bgcolor: isDisabled ? 'transparent' : 'action.hover',
                  '& .MuiListItemIcon-root': {
                    color: 'primary.main',
                  },
                },
                transition: 'all 0.2s ease',
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: collapsed ? 0 : 40,
                  color: isActive ? 'primary.main' : 'text.secondary',
                  transition: 'color 0.2s ease',
                  justifyContent: 'center',
                }}
              >
                <Icon sx={{ fontSize: 20 }} />
              </ListItemIcon>
              {!collapsed && (
                <>
                  <ListItemText
                    primary={t(item.label)}
                    primaryTypographyProps={{
                      fontSize: '0.9rem',
                      fontWeight: isActive ? 600 : 500,
                      letterSpacing: '-0.01em',
                      color: isActive ? 'primary.main' : 'text.secondary',
                    }}
                  />
                  {isActive && (
                    <Box
                      sx={{
                        width: 4,
                        height: 16,
                        bgcolor: 'primary.main',
                        borderRadius: '2px',
                        ml: 1,
                      }}
                    />
                  )}
                </>
              )}
            </ListItemButton>
          );

          return (
            <ListItem key={item.href} disablePadding sx={{ mb: 0.5 }}>
              <Tooltip
                title={getTooltipTitle()}
                placement="bottom-start"
                followCursor
                disableHoverListener={!isDisabled && !collapsed}
              >
                <Box sx={{ width: '100%' }}>{button}</Box>
              </Tooltip>
            </ListItem>
          );
        })}
    </List>
  );

  return (
    <Box id="sidebar-nav-step">
      {renderNavGroup(navItems.main)}
      {activeRole !== 'reader' && (
        <>
          {renderNavGroup(navItems.workspace, t('sidebar.sections.workspace'))}
          {renderNavGroup(navItems.worldBuilding, t('sidebar.sections.world_building'))}
          {renderNavGroup(navItems.planningTools, t('sidebar.sections.planning_tools'))}
          {renderNavGroup(navItems.other)}
        </>
      )}
    </Box>
  );
}
